import { FileNameTypes, FileTypes, FileUploadStatus } from '@/__generated__/graphql';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { Image } from '@/components/Analysis/shared/Image';
import { abyssColors } from '@/theme/colors';
import { theme } from '@/theme/theme';
import CloseIcon from '@mui/icons-material/Close';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import { Box, CircularProgress, Stack, Typography, Card, Tooltip, SxProps } from '@mui/material';
import { useMemo } from 'react';
import { AttachmentCaption } from '../CaptionBox/AttachmentCaption';
import { AttachmentMenu } from './AttachmentMenu';
import { AttachmentCard, StyledRemoveButton } from './styles';
import { Attachment } from './type';
import { KPI_ATTACHMENTS_COLLECTION_NAME } from '@/modules/KpiTracker/constants';

type Layout = 'card' | 'list';

type AttachmentDisplayProps = {
  collectionName: string;
  selectedAttachment: Attachment;
  selectedAttachmentIndex: number;
  showRemoveIcon: boolean;
  handleRemoveAttachment?: (index: number) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  attachmentCaption: string | undefined;
  setAttachmentCaption: (caption: string) => void;
  handleAttachmentCaption: () => void;
  handleCaption: (e: React.MouseEvent) => void;
  uploadProgress?: number;
  enableUpload?: boolean;
  cardHeight?: string;
  handleClick?: (e: React.MouseEvent, attachment?: Attachment) => void;
  isUploadDialog?: boolean;
  hasError?: boolean;
  handleError?: (success: boolean) => void;
  location?: string;
  handleSBSMode?: () => void;
  handleDownloadAttachment?: (index: number) => void;
  layout?: Layout;
  isRemovingDisabled?: boolean;
  readOnly?: boolean;
  sx?: SxProps;
  fallbackText?: string;
  disableEnterSave?: boolean;
  onCaptionChange?: (caption: string) => void;
};

export const AttachmentDisplay = ({
  collectionName,
  selectedAttachment,
  selectedAttachmentIndex,
  showRemoveIcon,
  handleRemoveAttachment,
  isEditing,
  setIsEditing,
  attachmentCaption,
  setAttachmentCaption,
  handleAttachmentCaption,
  handleCaption,
  uploadProgress = 0,
  cardHeight = '10rem',
  handleClick,
  isUploadDialog,
  hasError,
  handleError,
  location = '',
  handleSBSMode,
  handleDownloadAttachment,
  layout = 'card',
  fallbackText,
  readOnly,
  isRemovingDisabled = false,
  disableEnterSave = false,
  onCaptionChange,
}: AttachmentDisplayProps) => {
  const isImage = useMemo(() => {
    return (
      selectedAttachment.type.includes('image/') ||
      [FileTypes.Jpeg, FileTypes.Png].includes(selectedAttachment.type as FileTypes)
    );
  }, [selectedAttachment.type]);

  const isPdf = useMemo(() => {
    return (
      selectedAttachment.type.includes('application/pdf') ||
      selectedAttachment.type === FileTypes.Pdf
    );
  }, [selectedAttachment.type]);

  const isVideo = useMemo(() => {
    return (
      selectedAttachment.type.includes('video/mp4') || selectedAttachment.type === FileTypes.Mp4
    );
  }, [selectedAttachment.type]);

  const isAwaitingUrl =
    !!selectedAttachment.url &&
    !!selectedAttachment.isNew &&
    selectedAttachment.uploadStatus === FileUploadStatus.Inprogress;

  const canViewSideBySide = useMemo(() => {
    return (
      selectedAttachment.fileNameType === FileNameTypes.ReferenceDrawing ||
      selectedAttachment.fileNameType === FileNameTypes.IsometricDrawing
    );
  }, [selectedAttachment.fileNameType]);

  const renderFileContent = () => {
    if (isAwaitingUrl) {
      return (
        <Stack
          className={`${location}-attachment`}
          sx={{
            display: 'flex',
            height: cardHeight,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: abyssColors.primary[100],
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              variant="determinate"
              value={uploadProgress}
              size={50}
              thickness={5}
              sx={{ color: abyssColors.secondary[500] }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" component="div" color="text.secondary">
                {`${Math.round(uploadProgress)}%`}
              </Typography>
            </Box>
          </Box>
        </Stack>
      );
    } else if (isImage) {
      return (
        <Image
          src={selectedAttachment.base64 ?? selectedAttachment.url ?? ''}
          alt={selectedAttachment.name}
          imgSx={{ height: cardHeight, objectFit: 'cover' }}
          sx={{
            height: cardHeight,
            cursor: hasError ? 'not-allowed' : 'pointer',
            pointerEvents: hasError ? 'all' : 'auto',
            aspectRatio: '1 / 1',
          }}
          onLoadEnd={handleError}
          fallbackSize="2.2rem"
          hideErrorMessage
          className={`${location}-attachment ${location}-attachment-image`}
        />
      );
    } else if (isPdf || isVideo) {
      return (
        <a
          href={selectedAttachment.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e: React.MouseEvent) => {
            if (isEditing) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <Stack
            sx={{
              display: 'flex',
              height: cardHeight,
              justifyContent: 'center',
              alignItems: 'center',
              aspectRatio: '1 / 1',
            }}
            className={`${location}-attachment ${location}-attachment-${isPdf ? 'pdf' : 'video'}`}
          >
            {isPdf && <AbyssIcon iconName="pdf-file" size="2xl" />}
            {isVideo && (
              <SmartDisplayIcon sx={{ fontSize: 45, color: theme.palette.text.secondary }} />
            )}
          </Stack>
        </a>
      );
    }
  };

  const renderListItem = () => {
    return (
      <Card
        sx={{
          p: '1.2rem',
          width: '100%',
          borderRadius: '0.4rem',
          border: `1px solid ${abyssColors.primary[200]}`,
          boxShadow: 'none',
          backgroundColor: 'transparent',
        }}
      >
        <Stack
          direction="row"
          spacing={1.2}
          alignItems="flex-start"
          onClick={(e: React.MouseEvent) => {
            // Stop propagation
            const target = e.target as HTMLElement;
            const isClickOnImage = target.closest('[data-image-container]') !== null;
            const isClickOnText = target.closest('[data-text-container]') !== null;
            if (!isClickOnImage && !isClickOnText) {
              e.stopPropagation();
            }
          }}
        >
          <Box
            sx={{
              width: '8.4rem',
              height: '8.4rem',
              borderRadius: '0.25rem',
              overflow: 'hidden',
              flexShrink: 0,
              border: `1px solid ${abyssColors.primary[200]}`,
              position: 'relative',
              pointerEvents: 'auto',
            }}
            data-image-container
          >
            {isAwaitingUrl ? (
              <Stack
                sx={{
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: abyssColors.primary[100],
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress
                    variant="determinate"
                    value={uploadProgress}
                    size={28}
                    thickness={4}
                    sx={{ color: abyssColors.secondary[500] }}
                  />
                  <Typography
                    variant="caption"
                    component="div"
                    color="text.secondary"
                    sx={{ position: 'absolute', fontWeight: 600, fontSize: '0.65rem' }}
                  >
                    {`${Math.round(uploadProgress)}%`}
                  </Typography>
                </Box>
              </Stack>
            ) : isImage ? (
              <Box
                onClick={(e: React.MouseEvent) => {
                  if (!isEditing && handleClick) {
                    handleClick(e, selectedAttachment);
                  }
                }}
                sx={{
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <Image
                  src={selectedAttachment.base64 ?? selectedAttachment.url ?? ''}
                  alt={selectedAttachment.name}
                  imgSx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                  }}
                  hideErrorMessage
                />
              </Box>
            ) : (
              (isPdf || isVideo) && (
                <a
                  href={selectedAttachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e: React.MouseEvent) => {
                    if (isEditing) e.stopPropagation();
                  }}
                >
                  <Stack
                    sx={{
                      display: 'flex',
                      width: '100%',
                      height: '100%',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    className={`${location}-attachment ${location}-attachment-${
                      isPdf ? 'pdf' : 'video'
                    }`}
                  >
                    {isPdf && <AbyssIcon iconName="pdf-file" size="2xl" />}
                    {isVideo && (
                      <SmartDisplayIcon
                        sx={{ fontSize: 45, color: theme.palette.text.secondary }}
                      />
                    )}
                  </Stack>
                </a>
              )
            )}
          </Box>

          <Stack
            spacing={0.4}
            sx={{ flex: 1, minWidth: 0, pr: 0.4, pointerEvents: 'auto' }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            data-text-container
          >
            <Tooltip title={selectedAttachment.name || ''} arrow>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'Roboto',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                  lineHeight: '1.8rem',
                  color: abyssColors.primary[600],
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedAttachment.name}
              </Typography>
            </Tooltip>

            {!selectedAttachment.isNew && (
              <AttachmentCaption
                isUploadDialog={isUploadDialog}
                collectionName={collectionName}
                isEditing={!readOnly}
                readOnly={readOnly}
                setIsEditing={setIsEditing}
                attachmentCaption={attachmentCaption ?? selectedAttachment.caption}
                setAttachmentCaption={setAttachmentCaption}
                handleAttachmentCaption={handleAttachmentCaption}
                handleCaption={handleCaption}
                selectedAttachment={selectedAttachment}
                location={location}
                fluid={true}
                fallbackText={fallbackText}
                disableEnterSave={disableEnterSave}
                onCaptionChange={onCaptionChange}
                sx={{ width: '100%' }}
              />
            )}
          </Stack>

          <Box
            sx={{ ml: 'auto', display: 'flex', alignItems: 'flex-start', pt: 0.1 }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            data-text-container
          >
            {handleRemoveAttachment && !readOnly ? (
              <Box
                aria-label="remove attachment"
                onClick={(event: React.MouseEvent) => {
                  if (isRemovingDisabled) return;
                  event.stopPropagation();
                  handleRemoveAttachment(selectedAttachmentIndex);
                }}
                sx={{
                  color: isRemovingDisabled ? abyssColors.primary[300] : abyssColors.primary[500],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isRemovingDisabled ? 'not-allowed' : 'pointer',
                  width: '1.6rem',
                  height: '1.6rem',
                  opacity: isRemovingDisabled ? 0.5 : 1,
                }}
              >
                <CloseIcon sx={{ fontSize: '1.2rem' }} />
              </Box>
            ) : (
              <AttachmentMenu
                isPdf={isPdf}
                isVideo={isVideo}
                variant="inline"
                canViewSideBySide={canViewSideBySide}
                handleSBSMode={handleSBSMode}
                handleRemoveAttachment={readOnly ? undefined : handleRemoveAttachment}
                handleDownloadAttachment={handleDownloadAttachment}
                index={selectedAttachmentIndex}
                location={location}
              />
            )}
          </Box>
        </Stack>
      </Card>
    );
  };

  if (layout === 'list') return renderListItem();

  return (
    <Stack alignItems="center" gap="0.4rem" width={cardHeight}>
      <Stack position="relative">
        <AttachmentCard
          sx={{ position: 'relative' }}
          onClick={(event) => {
            if (handleClick) handleClick(event, selectedAttachment);
          }}
          isPdf={isPdf}
          isVideo={isVideo}
          cardHeight={cardHeight}
        >
          {renderFileContent()}
          {!showRemoveIcon && collectionName !== KPI_ATTACHMENTS_COLLECTION_NAME && (
            <AttachmentMenu
              isPdf={isPdf}
              isVideo={isVideo}
              canViewSideBySide={canViewSideBySide}
              handleSBSMode={handleSBSMode}
              handleRemoveAttachment={readOnly ? undefined : handleRemoveAttachment}
              handleDownloadAttachment={handleDownloadAttachment}
              index={selectedAttachmentIndex}
              location={location}
            />
          )}
        </AttachmentCard>
        {showRemoveIcon && handleRemoveAttachment && !readOnly && (
          <StyledRemoveButton
            aria-label="delete"
            onClick={() => handleRemoveAttachment(selectedAttachmentIndex)}
          >
            <CloseIcon style={{ fontSize: '13px' }} />
          </StyledRemoveButton>
        )}
      </Stack>
      <AttachmentCaption
        isUploadDialog={isUploadDialog}
        collectionName={collectionName}
        isEditing={isEditing}
        readOnly={readOnly}
        setIsEditing={setIsEditing}
        attachmentCaption={attachmentCaption}
        setAttachmentCaption={setAttachmentCaption}
        handleAttachmentCaption={handleAttachmentCaption}
        handleCaption={handleCaption}
        selectedAttachment={selectedAttachment}
        location={location}
        fluid={true}
        disableEnterSave={disableEnterSave}
        onCaptionChange={onCaptionChange}
        sx={{ width: '100%' }}
      />
    </Stack>
  );
};
